/**
 * Runtime configuration for the client.
 *
 * In production (Cloudflare Pages), `vibe-config.json` is uploaded alongside
 * the static bundle. It contains the CURRENT *.trycloudflare.com URLs for
 * the Colyseus engine and the agent-service (the URLs rotate whenever the
 * cloudflared quick-tunnel process restarts on the droplet, so they can't
 * be baked at build time).
 *
 * In dev (vite at :5173), the file is absent and we fall back to localhost
 * defaults — same behavior the client had before wave 3I.
 *
 * Build-time overrides (VITE_SERVER_URL / VITE_AGENT_URL) take precedence
 * if set — useful for previews / staging.
 */

export interface VibeRuntimeConfig {
  /** WebSocket endpoint for the Colyseus server, e.g. wss://foo.trycloudflare.com */
  serverUrl: string;
  /** HTTP base URL for the agent-service, e.g. https://bar.trycloudflare.com */
  agentUrl: string;
  /**
   * HTTP base URL for the engine's /auth endpoint. Same host as serverUrl
   * but http(s) instead of ws(s). Pre-deploy-client wave: derived in code if
   * the deployed vibe-config.json doesn't include the field (back-compat).
   */
  authUrl: string;
}

const CONFIG_PATH = "/vibe-config.json";

function buildDevDefault(): VibeRuntimeConfig {
  // Computed lazily — at import time, jsdom-less tests don't have a `location`
  // global. Real browsers always do.
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  const isLocal = location.hostname === "localhost" || location.hostname === "127.0.0.1";
  return {
    serverUrl: `${proto}//${isLocal ? `${location.hostname}:2567` : location.host}`,
    agentUrl: `${location.protocol}//${isLocal ? `${location.hostname}:3001` : location.host}`,
    authUrl: `${location.protocol}//${isLocal ? `${location.hostname}:2567` : location.host}`,
  };
}

/**
 * Derive the auth URL from a Colyseus WebSocket URL. Same host, https/http
 * scheme. Used as a fallback when the deployed vibe-config.json predates
 * the authUrl field.
 */
function deriveAuthUrl(serverUrl: string): string {
  if (serverUrl.startsWith("wss://")) return "https://" + serverUrl.slice("wss://".length);
  if (serverUrl.startsWith("ws://")) return "http://" + serverUrl.slice("ws://".length);
  return serverUrl;
}

let cached: VibeRuntimeConfig | null = null;

/**
 * Load runtime config. Order of precedence:
 *   1. Vite build-time env vars (VITE_SERVER_URL / VITE_AGENT_URL).
 *   2. /vibe-config.json fetched from same origin (Cloudflare Pages prod).
 *   3. Dev localhost defaults.
 *
 * Never throws — on fetch failure we log and return the dev default. The
 * subsequent `joinRoom` call retries forever, so a stale config still
 * surfaces visibly to the user as "offline — retrying" rather than as a
 * silent crash.
 */
export async function loadConfig(): Promise<VibeRuntimeConfig> {
  if (cached) return cached;

  const envServer = (import.meta as ImportMeta & { env?: Record<string, string | undefined> })
    .env?.VITE_SERVER_URL;
  const envAgent = (import.meta as ImportMeta & { env?: Record<string, string | undefined> })
    .env?.VITE_AGENT_URL;
  const envAuth = (import.meta as ImportMeta & { env?: Record<string, string | undefined> })
    .env?.VITE_AUTH_URL;
  if (envServer && envAgent) {
    cached = {
      serverUrl: envServer,
      agentUrl: envAgent,
      authUrl: envAuth ?? deriveAuthUrl(envServer),
    };
    return cached;
  }

  try {
    // Bust any CDN cache: include a timestamp. Pages serves vibe-config.json
    // with default caching, so a fresh deploy might briefly serve a stale URL
    // — the cache-buster makes the browser bypass the SW/CDN copy.
    const res = await fetch(`${CONFIG_PATH}?t=${Date.now()}`, { cache: "no-store" });
    if (res.ok) {
      const json = (await res.json()) as Partial<VibeRuntimeConfig>;
      if (json && typeof json.serverUrl === "string" && typeof json.agentUrl === "string") {
        cached = {
          serverUrl: envServer ?? json.serverUrl,
          agentUrl: envAgent ?? json.agentUrl,
          authUrl:
            envAuth ??
            (typeof json.authUrl === "string" ? json.authUrl : deriveAuthUrl(json.serverUrl)),
        };
        return cached;
      }
      // eslint-disable-next-line no-console
      console.warn("[config] vibe-config.json malformed, using defaults", json);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[config] could not load ${CONFIG_PATH}, using dev defaults`, err);
  }

  const dev = buildDevDefault();
  cached = {
    serverUrl: envServer ?? dev.serverUrl,
    agentUrl: envAgent ?? dev.agentUrl,
    authUrl: envAuth ?? dev.authUrl,
  };
  return cached;
}

/** Synchronous access for code paths that need a value after `loadConfig()` resolved. */
export function getConfig(): VibeRuntimeConfig {
  if (!cached) {
    throw new Error("loadConfig() must be awaited before getConfig()");
  }
  return cached;
}

/** Test-only: reset the cache so each test sees a fresh load. */
export function resetConfigForTests(): void {
  cached = null;
}
